import { useSceneEvents } from "@/components/common/scene-events-context";
import type { CameraAnimationProps } from "@/components/r3f/animations/camera-animation";
import { computeLookAtQuaternion } from "@/modes/overview-mode/use-update-overview-camera";
import { selectFlyWalkSceneFilter } from "@/store/modes/flywalk-mode-slice";
import { selectActiveElement } from "@/store/selections-selectors";
import { useAppStore } from "@/store/store-hooks";
import { SceneFilter } from "@/types/scene-filter";
import { CameraView, CameraViewType, SetCameraView } from "@/utils/cam-view";
import { useTypedEvent } from "@faro-lotv/app-component-toolbox";
import {
  isIElementImg360,
  isIElementModel3dStream,
} from "@faro-lotv/ielement-types";
import { useThree } from "@react-three/fiber";
import { RefObject, useCallback } from "react";
import { Camera, Matrix4, Quaternion, Vector3 } from "three";
import { FlyWalkSceneActiveElement } from "../flywalk-types";
import { FlyWalkAnimation } from "./flywalk-animation";
import { TargetCameraView } from "./flywalk-to-pano-animation";

/**
 * This hook contains the logic needed to support correctly go-to-annotation actions.
 *
 * @param main The current main object
 * @param camera The camera that is rendering the scene
 * @param setFlyWalkAnimation Callback to set the animation result of this function
 * @param setTarget Callback to set the current rotation pivot
 * @param pendingEvent An object to store an event that is yet to be executed
 * @returns A callback to start a camera animation to a predefined view
 */
export function useReactToSceneEvents(
  main: FlyWalkSceneActiveElement | null,
  camera: Camera,
  setFlyWalkAnimation: (animation: FlyWalkAnimation | null) => void,
  setTarget: (target: Vector3 | undefined) => void,
  pendingEvent: RefObject<TargetCameraView | null>,
): SetCameraView {
  const { lookAt } = useSceneEvents();

  const { getState } = useAppStore();

  const onLookAt = useCallback(
    (target: Vector3) => {
      const activeElement = selectActiveElement(getState());
      if (activeElement && isIElementImg360(activeElement)) {
        // If we enter here, we are transitioning towards a pano image
        const mainId = main?.id;
        const activeElementId = activeElement.id;
        if (mainId !== activeElementId && activeElementId) {
          // A transition is about to happen, to bring 'main' to 'activeElementId'.
          // Saving the 'lookAtPosition' for then.
          pendingEvent.current = {
            elementId: activeElementId,
            lookAtPosition: target,
          };
          return;
        }
        const quaternion = new Quaternion().setFromRotationMatrix(
          new Matrix4().lookAt(camera.position, target, camera.up),
        );
        setFlyWalkAnimation({
          cameraAnimation: {
            quaternion,
          },
        });
        return;
      }
      const viewDir = target.clone().sub(camera.position).normalize();
      const position = target.clone().sub(viewDir.multiplyScalar(5));
      const quaternion = computeLookAtQuaternion(target, target);

      setTarget(target);
      setFlyWalkAnimation({
        cameraAnimation: {
          position,
          quaternion,
        },
      });
    },
    [camera, main, setFlyWalkAnimation, getState, setTarget, pendingEvent],
  );

  useTypedEvent(lookAt, onLookAt);

  const aspectRatio = useThree((s) => s.size.width / s.size.height);

  const onSetCameraView = useCallback(
    (cameraView: CameraView) => {
      const state = getState();
      const activeElement = selectActiveElement(state);
      const sceneFilter = selectFlyWalkSceneFilter(state);
      if (activeElement && isIElementImg360(activeElement)) {
        // If we enter here, we are transitioning towards a pano image
        const mainId = main?.id;
        const activeElementId = activeElement.id;
        if (mainId !== activeElementId && activeElementId) {
          // A transition is about to happen, to bring 'main' to 'activeElementId'.
          // Saving the 'cameraView' for then.
          pendingEvent.current = {
            elementId: activeElementId,
            cameraView,
          };
          return;
        }
        setFlyWalkAnimation({
          cameraAnimation: {
            quaternion: cameraView.rotation,
            fov: cameraView.zoomLevel,
          },
        });
        return;
      }
      setTarget(cameraView.target);
      if (
        main &&
        !isIElementModel3dStream(main) &&
        sceneFilter === SceneFilter.Cad
      ) {
        // The main is going to transition to the CAD model.
        // Saving the 'cameraView' for then.
        pendingEvent.current = {
          elementId: main.id,
          cameraView,
          aspectRatio,
        };
        return;
      }
      setFlyWalkAnimation({
        cameraAnimation: cameraView2cameraAnimation(cameraView, aspectRatio),
      });
    },
    [main, setFlyWalkAnimation, getState, pendingEvent, setTarget, aspectRatio],
  );

  return onSetCameraView;
}

/**
 *
 * @param cameraView The input camera view
 * @param aspectRatio The current view aspect ratio
 * @returns A camera animation suitable to reach the input camera view
 */
export function cameraView2cameraAnimation(
  cameraView: CameraView,
  aspectRatio: number,
): CameraAnimationProps {
  const isOrtho = cameraView.type === CameraViewType.orthographic;
  return {
    position: cameraView.position,
    quaternion: cameraView.rotation,
    fov: isOrtho ? undefined : cameraView.zoomLevel,
    frustum: isOrtho
      ? {
          top: cameraView.zoomLevel * 0.5,
          right: cameraView.zoomLevel * 0.5 * aspectRatio,
          bottom: -cameraView.zoomLevel * 0.5,
          left: -cameraView.zoomLevel * 0.5 * aspectRatio,
          // The bbox center prop is not used anyway during the camera animation.
          bboxCenter: cameraView.target ?? new Vector3(),
        }
      : undefined,
  };
}
